import { InspectorControls, useBlockProps, BlockControls } from "@wordpress/block-editor";
import { Button, ButtonGroup, Disabled, PanelBody, SelectControl, TextControl, TextareaControl, ToggleControl, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useEffect, useRef } from "@wordpress/element";
import { useSelect } from "@wordpress/data";
import { __, sprintf } from "@wordpress/i18n";
import { isDuplicateTocAnchor } from "../../../shared/js/tocAnchorDiagnostics";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import ProductRefsControl from "../../../shared/js/ProductRefsControl";
import useCeggEditorProducts from "../../../shared/js/useCeggEditorProducts";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Compact", "content-egg"), value: "compact" },
    { label: __("Alternatives", "content-egg"), value: "alternatives" },
    { label: __("Shelves", "content-egg"), value: "shelves" },
    { label: __("Grid", "content-egg"), value: "grid" },
    { label: __("Highlight", "content-egg"), value: "highlight" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

function createItemId() {
    return `qp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const snapshot = useCeggEditorProducts();
    const {
        variant,
        section_label,
        title,
        heading_tag,
        cta_label,
        items = [],
        anchor,
        toc_label,
        include_in_toc,
        level,
        color_scheme,
    } = attributes;
    const allBlocks = useSelect((select) => select("core/block-editor").getBlocks(), []);
    const hasDuplicateTocAnchor = !!include_in_toc && isDuplicateTocAnchor(allBlocks, anchor);
    const suggestedTocLabel = (title || section_label || "").trim();

    const seededValuesRef = useRef({});

    useEffect(() => {
        const nextItems = [...(items || [])];
        const nextSeededValues = {};
        let hasChanges = false;

        nextItems.forEach((item, index) => {
            const itemId = item?.id || createItemId();
            if (!item?.id) {
                nextItems[index] = { ...item, id: itemId };
                hasChanges = true;
            }

            const ref = item?.product_ref || {};
            const productKey = `${String(ref.module_id || "")}:${String(ref.unique_id || "")}`;
            const product = (snapshot.all || []).find((candidate) => (
                `${String(candidate.module_id || "")}:${String(candidate.unique_id || "")}` === productKey
            )) || null;

            if (!product || productKey === ":") {
                return;
            }

            const previousSeeded = seededValuesRef.current[itemId] || {
                productKey: "",
                title: "",
                subtitle: "",
                merchant: "",
                badge: "",
            };
            const nextSeeded = {
                productKey,
                title: String(product.title || "").trim(),
                subtitle: String(product.subtitle || "").trim(),
                merchant: String(product.merchant || product.domain || "").trim(),
                badge: String(product.badge || "").trim(),
            };
            nextSeededValues[itemId] = nextSeeded;

            if (previousSeeded.productKey === productKey) {
                return;
            }

            if (String(nextItems[index]?.seeded_for || "") === productKey) {
                return;
            }

            const currentTitle = String(nextItems[index]?.title || "").trim();
            const currentSubtitle = String(nextItems[index]?.subtitle || "").trim();
            const currentMerchant = String(nextItems[index]?.merchant || "").trim();
            const currentBadge = String(nextItems[index]?.badge || "").trim();
            const nextPatch = {};

            if (nextSeeded.title !== "" && (currentTitle === "" || currentTitle === previousSeeded.title)) {
                nextPatch.title = nextSeeded.title;
            }

            if (nextSeeded.subtitle !== "" && (currentSubtitle === "" || currentSubtitle === previousSeeded.subtitle)) {
                nextPatch.subtitle = nextSeeded.subtitle;
            }

            if (nextSeeded.merchant !== "" && (currentMerchant === "" || currentMerchant === previousSeeded.merchant)) {
                nextPatch.merchant = nextSeeded.merchant;
            }

            if (nextSeeded.badge !== "" && (currentBadge === "" || currentBadge === previousSeeded.badge)) {
                nextPatch.badge = nextSeeded.badge;
            }

            nextPatch.seeded_for = productKey;

            nextItems[index] = {
                ...nextItems[index],
                ...nextPatch,
            };
            hasChanges = true;
        });

        seededValuesRef.current = nextSeededValues;

        if (hasChanges) {
            setAttributes({ items: nextItems });
        }
    }, [items, setAttributes, snapshot.all]);

    const updateItems = (nextItems) => setAttributes({ items: nextItems });
    const hasPreviewContent = Array.isArray(items) && items.length > 0;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Product Binding", "content-egg")} initialOpen={!hasPreviewContent}>
                    <ProductRefsControl
                        items={items}
                        onChange={updateItems}
                        preserveUnboundRows
                        emptyMessage={__("No quick-picks products are currently bound to this block.", "content-egg")}
                        createEmptyItem={(productRef) => ({
                            id: createItemId(),
                            product_ref: productRef,
                            title: "",
                            subtitle: "",
                            merchant: "",
                            badge: "",
                            description: "",
                            chips: [],
                            score: "",
                        })}
                        renderItemFields={({ item, updateItem }) => (
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <TextControl
                                    label={__("Title", "content-egg")}
                                    value={item?.title || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        title: value,
                                    }))}
                                />
                                <TextControl
                                    label={__("Subtitle", "content-egg")}
                                    value={item?.subtitle || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        subtitle: value,
                                    }))}
                                />
                                <TextControl
                                    label={__("Merchant", "content-egg")}
                                    value={item?.merchant || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        merchant: value,
                                    }))}
                                />
                                <TextControl
                                    label={__("Badge", "content-egg")}
                                    value={item?.badge || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        badge: value,
                                    }))}
                                />
                                <TextareaControl
                                    label={__("Description", "content-egg")}
                                    value={item?.description || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        description: value,
                                    }))}
                                    rows={3}
                                />
                                <TextControl
                                    label={__("Score", "content-egg")}
                                    value={item?.score || ""}
                                    onChange={(value) => updateItem((currentItem) => ({
                                        ...currentItem,
                                        score: value,
                                    }))}
                                />

                                <div>
                                    <div style={{ fontSize: "11px", fontWeight: 600, marginBottom: "6px" }}>
                                        {__("Chips", "content-egg")}
                                    </div>
                                    {(item?.chips || []).map((chip, chipIndex) => (
                                        <div key={chipIndex} style={{ display: "flex", gap: "4px", marginBottom: "6px" }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <TextControl
                                                    value={chip}
                                                    onChange={(value) => updateItem((currentItem) => {
                                                        const nextChips = [...(currentItem?.chips || [])];
                                                        nextChips[chipIndex] = value;
                                                        return {
                                                            ...currentItem,
                                                            chips: nextChips,
                                                        };
                                                    })}
                                                    __nextHasNoMarginBottom
                                                />
                                            </div>
                                            <Button
                                                icon="no-alt"
                                                isDestructive
                                                size="small"
                                                onClick={() => updateItem((currentItem) => {
                                                    const nextChips = [...(currentItem?.chips || [])];
                                                    nextChips.splice(chipIndex, 1);
                                                    return {
                                                        ...currentItem,
                                                        chips: nextChips,
                                                    };
                                                })}
                                            />
                                        </div>
                                    ))}
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() => updateItem((currentItem) => ({
                                            ...currentItem,
                                            chips: [...(currentItem?.chips || []), ""],
                                        }))}
                                    >
                                        {__("Add chip", "content-egg")}
                                    </Button>
                                </div>
                            </div>
                        )}
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Section Label", "content-egg")}
                        value={section_label}
                        onChange={(value) => setAttributes({ section_label: value })}
                    />
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {["h1", "h2", "h3", "h4", "div"].map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <TextControl
                        label={__("CTA Label", "content-egg")}
                        value={cta_label}
                        onChange={(value) => setAttributes({ cta_label: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("TOC", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Anchor", "content-egg")}
                        value={anchor || ""}
                        onChange={(value) => setAttributes({ anchor: value })}
                    />
                    {hasDuplicateTocAnchor && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#8a1f11", fontSize: "12px" }}>
                            {__("This anchor is already used by another TOC block.", "content-egg")}
                        </p>
                    )}
                    <ToggleControl
                        label={__("Include In TOC", "content-egg")}
                        checked={!!include_in_toc}
                        onChange={(value) => setAttributes({ include_in_toc: value })}
                    />
                    {include_in_toc && !(anchor || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {__("Set an anchor so the TOC can link to this block.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("TOC Label", "content-egg")}
                        value={toc_label || ""}
                        onChange={(value) => setAttributes({ toc_label: value })}
                    />
                    {include_in_toc && !(toc_label || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {suggestedTocLabel
                                ? sprintf(__("Suggested label: %s", "content-egg"), `"${suggestedTocLabel}"`)
                                : __("Set a TOC label so this block can appear in automatic TOC mode.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("Level", "content-egg")}
                        type="number"
                        min={1}
                        max={6}
                        value={level ?? 2}
                        onChange={(value) => setAttributes({ level: value === "" ? 2 : Number(value) })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/quick-picks"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Bind Content Egg products to preview this block.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
